import React from "react";

import { useAuth } from "@/contexts/AuthContext";
import InventoryService from "@/services/features/inventory/InventoryService";

export interface ProductDetails {
  title: string;
  imageUrl: string;
  price: string;
  salePrice?: string;
  color: string;
  size: string;
}

const productDetailsCache = new Map<string, ProductDetails | null>();
const pendingProductDetailsRequests = new Map<string, Promise<ProductDetails | null>>();

const getCacheKey = (storeId: number, sku: string) => `${storeId}:${sku}`;

export const useProductDetails = (sku: string | undefined, storeId: number | undefined) => {
  const { user } = useAuth();
  const cacheKey = sku && storeId ? getCacheKey(storeId, sku) : null;
  const [productDetails, setProductDetails] = React.useState<ProductDetails | null>(() =>
    cacheKey && productDetailsCache.has(cacheKey)
      ? (productDetailsCache.get(cacheKey) ?? null)
      : null,
  );

  React.useEffect(() => {
    if (!cacheKey || !sku || !storeId || !user?.token) {
      setProductDetails(null);
      return;
    }

    if (productDetailsCache.has(cacheKey)) {
      setProductDetails(productDetailsCache.get(cacheKey) ?? null);
      return;
    }

    let isActive = true;
    const pendingRequest = pendingProductDetailsRequests.get(cacheKey);
    const request =
      pendingRequest ??
      new InventoryService(user.token)
        .fetchProductDetails(storeId, sku)
        .then((response): ProductDetails | null => {
          const product = response.data.skus.find((candidate) => candidate.skuId === sku);
          return product
            ? {
                title: product.title,
                imageUrl: product.imageUrl,
                price: product.price,
                salePrice: product.salePrice || undefined,
                color: product.color,
                size: product.size,
              }
            : null;
        })
        .finally(() => {
          pendingProductDetailsRequests.delete(cacheKey);
        });

    if (!pendingRequest) {
      pendingProductDetailsRequests.set(cacheKey, request);
    }

    void request
      .then((details) => {
        productDetailsCache.set(cacheKey, details);
        if (isActive) {
          setProductDetails(details);
        }
      })
      .catch(() => {
        if (isActive) {
          setProductDetails(null);
        }
      });

    return () => {
      isActive = false;
    };
  }, [cacheKey, sku, storeId, user?.token]);

  return productDetails;
};
